import {
  collection,
  doc,
  query,
  serverTimestamp,
  where,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { ZodError } from 'zod'
import type { FirestoreUserProfileFavoriteSlideDeck } from './schema'
import { schema } from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { StreamInterface } from 'tricklejs/dist/types'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import { UserProfileFavoriteSlideDeck } from '../../models/UserProfileFavoriteSlideDeck'
import {
  deleteDocWithError,
  getDocsWithError,
  setDocWithError,
} from '../../firestore-mobx/fetch'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreUserProfileFavoriteSlideDeck> =
  {
    toFirestore: (data) => data,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      try {
        return schema.parse(data)
      } catch (e) {
        if (e instanceof ZodError) {
          console.error('Error parsing favorite slide deck', e.errors)
        }
        throw e
      }
    },
  }

const getColRef = (
  firestore: Firestore,
  userId: string
): CollectionReference<FirestoreUserProfileFavoriteSlideDeck, DocumentData> => {
  return collection(
    firestore,
    'user_profile',
    userId,
    'favorite_slide_deck'
  ).withConverter(converter)
}

export const fetchUserProfileFavoriteSlideDecks = async (
  repository: FirebaseRepository,
  params: { userId: string }
): Promise<UserProfileFavoriteSlideDeck[]> => {
  const ref = getColRef(repository.firestore, params.userId)
  const res = await getDocsWithError(ref)
  return res.docs.map((doc) =>
    convertDocumentSnapshotToModel(
      repository,
      doc,
      UserProfileFavoriteSlideDeck
    )
  )
}

export const getUserProfileFavoriteSlideDecks = (
  repository: FirebaseRepository,
  params: { userId: string }
): StreamInterface<UserProfileFavoriteSlideDeck[]> => {
  const ref = getColRef(repository.firestore, params.userId)

  return modelListStream(repository, ref, UserProfileFavoriteSlideDeck)
}

export const getUserProfileFavoriteSlideDeck = (
  repository: FirebaseRepository,
  params: { userId: string; slideDeckId: string }
): StreamInterface<UserProfileFavoriteSlideDeck[]> => {
  const ref = getColRef(repository.firestore, params.userId)

  const q = query(ref, where('slideDeckId', '==', params.slideDeckId))

  return modelListStream(repository, q, UserProfileFavoriteSlideDeck)
}

export const addUserProfileFavoriteSlideDeck = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    slideDeckId: string
    slideDeckTypeId: string
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, params.userId)
  const docRef = doc(ref, params.slideDeckId)

  await setDocWithError(docRef, {
    slideDeckId: params.slideDeckId,
    slideDeckTypeId: params.slideDeckTypeId,
    userId: params.userId,
    updatedAt: serverTimestamp(),
  })
}

export const removeUserProfileFavoriteSlideDeck = async (
  repository: FirebaseRepository,
  params: {
    userId: string
    slideDeckId: string
  }
): Promise<void> => {
  const ref = doc(
    getColRef(repository.firestore, params.userId),
    params.slideDeckId
  )

  return deleteDocWithError(ref)
}
